import { useApplication } from '@pixi/react';
import { useEffect, useState, type ReactNode } from 'react';

interface CoverSpaceProps {
  /** Intrinsic size of the background image the children are placed on. */
  width: number;
  height: number;
  children?: ReactNode;
}

interface Transform {
  scale: number;
  x: number;
  y: number;
}

/**
 * A container in background-image pixel coordinates. It applies the same transform as CSS
 * `background-size: cover; background-position: center`, so effects line up with painted features.
 */
export function CoverSpace({ width, height, children }: CoverSpaceProps) {
  const { app, isInitialised } = useApplication();
  const [transform, setTransform] = useState<Transform>({ scale: 1, x: 0, y: 0 });

  useEffect(() => {
    // The renderer exists only between init and destroy; React StrictMode remounts in between.
    const renderer = isInitialised ? (app.renderer as typeof app.renderer | undefined) : undefined;
    if (!renderer) return;
    const update = () => {
      const { width: sw, height: sh } = renderer.screen;
      const scale = Math.max(sw / width, sh / height);
      setTransform({ scale, x: (sw - width * scale) / 2, y: (sh - height * scale) / 2 });
    };
    update();
    renderer.on('resize', update);
    return () => {
      renderer.off('resize', update);
    };
  }, [app, isInitialised, width, height]);

  return (
    <pixiContainer x={transform.x} y={transform.y} scale={transform.scale}>
      {children}
    </pixiContainer>
  );
}
